import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Sparkles, Clock, ExternalLink, RefreshCw, AlertCircle, CheckCircle } from "lucide-react";
import { ESTADO_LABELS, ESTADO_CLS } from "./shared";

/**
 * Widget mini que muestra las últimas 5 licitaciones detectadas.
 *
 * Consume GET /api/tender360/m0/external-opportunities/latest?limit=5
 * ordenado por created_at DESC (más reciente arriba). Cada fila:
 *   [badge fuente]  Título (truncado)          score · deadline
 *
 * Se pensó para colocarse al lado del SyncHealthByScraper.
 */
const SOURCE_STYLES = {
  placsp:  { cls: "bg-emerald-100 text-emerald-800", icon: "🇪🇸" },
  euskadi: { cls: "bg-rose-100 text-rose-800",       icon: "🟥" },
  xunta:   { cls: "bg-sky-100 text-sky-800",         icon: "🟢" },
  boe:     { cls: "bg-amber-100 text-amber-800",     icon: "📜" },
  ted:     { cls: "bg-indigo-100 text-indigo-800",   icon: "🇪🇺" },
  scraping:{ cls: "bg-purple-100 text-purple-800",   icon: "🕸️" },
};

const fmtEUR = (n) => n == null ? "—" : `${Number(n).toLocaleString("es-ES", { maximumFractionDigits: 0 })} €`;

const fmtRelTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffHours = (Date.now() - d.getTime()) / 3_600_000;
  if (diffHours < 1) return `hace ${Math.max(1, Math.round(diffHours * 60))} min`;
  if (diffHours < 48) return `hace ${Math.round(diffHours)} h`;
  return `hace ${Math.round(diffHours / 24)} d`;
};

export function LatestOpportunities({ limit = 5 }) {
  const [docs, setDocs] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("");
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (stage) params.set("stage", stage);
      const { data } = await axios.get(
        `${API}/tender360/m0/external-opportunities/latest?${params.toString()}`
      );
      setDocs(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [limit, stage]);

  const openRow = (d) => {
    if (d.converted_to_tender_id) {
      navigate(`/licitaciones/${d.converted_to_tender_id}`);
    } else if (d.url) {
      window.open(d.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      className="mt-3 p-3 rounded-xl bg-white border border-gray-100"
      data-testid="latest-opportunities-widget"
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-purple-600" />
          <p className="text-xs font-bold text-gray-800 font-['Outfit']">
            Últimas {limit} licitaciones detectadas
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            data-testid="latest-opportunities-stage-filter"
            title="Filtrar por fase del pipeline CRM"
            className="text-[10px] border border-gray-200 rounded px-1.5 py-0.5 bg-white text-gray-700 focus:outline-none focus:border-indigo-400"
          >
            <option value="">Todas</option>
            <option value="not_converted">Sin convertir</option>
            <option value="converted">Convertidas · todas</option>
            <option value="identificada">Identificada</option>
            <option value="en_analisis">En análisis</option>
            <option value="oferta_preparacion">Preparando oferta</option>
            <option value="oferta_presentada">Presentada</option>
            <option value="adjudicada">Adjudicada</option>
            <option value="ganada">Ganada</option>
            <option value="desestimada">Desestimada</option>
            <option value="no_presentada">No presentada</option>
          </select>
          <button
            onClick={fetchData}
            disabled={loading}
            data-testid="latest-opportunities-refresh"
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
            title="Refrescar"
          >
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {err && (
        <p className="text-[10px] text-red-500 italic flex items-center gap-1">
          <AlertCircle size={10} /> {err}
        </p>
      )}
      {loading && !docs && (
        <p className="text-[11px] text-gray-400 py-2">Cargando…</p>
      )}
      {docs && docs.length === 0 && (
        <p className="text-[11px] text-gray-400 py-2 italic" data-testid="latest-opportunities-empty">
          {stage ? "No hay oportunidades para este filtro." : "Aún no hay oportunidades detectadas."}
        </p>
      )}

      <div className="space-y-1.5">
        {(docs || []).map(d => {
          const style = SOURCE_STYLES[d.source] || { cls: "bg-gray-100 text-gray-600", icon: "" };
          const isConverted = Boolean(d.converted_to_tender_id);
          return (
            <div
              key={d.id || d.external_id}
              data-testid={`latest-opp-${d.id || d.external_id}`}
              onClick={() => openRow(d)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openRow(d); } }}
              title={isConverted ? "Ver detalle de la licitación" : "Abrir en la fuente"}
              className="flex items-start gap-2 py-1.5 border-b border-gray-50 last:border-0 group hover:bg-indigo-50/50 -mx-1 px-1 rounded transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              <span
                className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase flex-shrink-0 ${style.cls}`}
                title={d.source}
              >
                {style.icon} {d.source}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-medium text-gray-800 line-clamp-2 leading-snug group-hover:text-indigo-700">
                  {d.title || d.external_id}
                </p>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-400">
                  <Clock size={9} />
                  <span>{fmtRelTime(d.created_at)}</span>
                  {d.value_eur != null && <><span>·</span><span className="text-gray-500 font-medium">{fmtEUR(d.value_eur)}</span></>}
                  {d.ai_score != null && (
                    <>
                      <span>·</span>
                      <span className={`font-semibold ${d.ai_score >= 70 ? "text-emerald-600" : d.ai_score >= 40 ? "text-amber-600" : "text-gray-500"}`}>
                        score {d.ai_score}
                      </span>
                    </>
                  )}
                  {isConverted && (
                    <>
                      <span>·</span>
                      <span className="flex items-center gap-0.5 text-emerald-600 font-semibold">
                        <CheckCircle size={9} /> convertida
                      </span>
                      {d.tender_estado_crm && (
                        <span
                          data-testid={`latest-opp-stage-${d.id || d.external_id}`}
                          title="Fase actual en el pipeline CRM"
                          className={`px-1.5 py-0.5 rounded font-semibold text-[9px] ${ESTADO_CLS[d.tender_estado_crm] || "bg-gray-100 text-gray-600"}`}
                        >
                          {ESTADO_LABELS[d.tender_estado_crm] || d.tender_estado_crm}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="opacity-0 group-hover:opacity-100 flex-shrink-0 mt-0.5 transition-all">
                {isConverted ? (
                  <span className="text-[10px] text-indigo-600 font-semibold whitespace-nowrap">
                    Ver detalle →
                  </span>
                ) : d.url ? (
                  <ExternalLink size={11} className="text-gray-400" />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
